import type {
  GridApi,
  IServerSideDatasource,
  IServerSideGetRowsParams,
  SortModelItem
} from 'ag-grid-community'
import { GRID_DEFAULTS } from '../constants'
import { calculatePageNumber } from './grid-config'

/**
 * Server-Side Row Model (SSRM) datasource for AG Grid
 * Handles pagination, data loading, and error handling for server-side data
 * Supports initial data caching for instant first render
 */

// Backend ALWAYS sends this exact format (from data_grid.ex:64-86)
type GridReply = {
  readonly data: readonly unknown[]
  readonly meta: {
    readonly current_page: number
    readonly next_page: number | null
    readonly page_size: number
    readonly total_count: number
    readonly total_pages: number
  }
}

// Pagination state detection result
type PaginationState = {
  readonly hasNextPage: boolean
  readonly totalCount: number
}

// Initial data cache for instant first render
type InitialDataCache = {
  readonly data: readonly Record<string, unknown>[]
  readonly meta?: GridReply['meta']
}

type GridRowsParams = Pick<
  IServerSideGetRowsParams<unknown>,
  'request' | 'success'
> &
  Partial<Pick<IServerSideGetRowsParams<unknown>, 'fail'>>

type RowLoad =
  | Readonly<{ status: 'waiting' }>
  | Readonly<{ status: 'loading'; generation: number }>

type AcceptedFirstPage = Readonly<{
  data: readonly unknown[]
  totalCount: number
}>

type GridTransport = Readonly<{
  pushEvent: (
    event: string,
    payload: { readonly id: string; readonly page: number },
    onReply: (reply: GridReply) => void
  ) => void
  pushEventTo: (
    target: string,
    event: string,
    payload: { readonly id: string; readonly page: number },
    onReply: (reply: GridReply) => void
  ) => void
}>

/**
 * Inject `__ssrm_row_index__` on every row so AG Grid's `getRowId` can hand
 * back a guaranteed-unique ID per row. Required because the default fallback
 * (`String(params.data.id ?? params.data)`) returns `"[object Object]"` for
 * any row without an `id` field — every such row collides, AG Grid silently
 * dedupes them, the visible block stays under-filled, and the SSRM cache
 * thrash-fetches the same blocks forever.
 */
const withRowIndex = (
  rows: readonly unknown[],
  startRow: number
): Record<string, unknown>[] =>
  rows.map((row, i) => ({
    ...(row as Record<string, unknown>),
    __ssrm_row_index__: startRow + i
  }))

// Return type exposing both the datasource and cache control
type DatasourceWithCache = {
  readonly datasource: Readonly<{
    getRows: (params: GridRowsParams) => void
    destroy: () => void
  }>
  /** Retire the datasource before the outgoing grid instance is released. */
  readonly cleanup: () => void
  /** Reject rows requested before the latest column or view change. */
  readonly invalidateCache: () => void
  /** Release requests only after their native store has been replaced. */
  readonly retireCache: () => void
  /** Resume current requests, optionally completing page one with accepted rows. */
  readonly resumeRequests: (firstPage?: AcceptedFirstPage) => void
  /** After replacing the native store, release outgoing loads and cache page 1.
   *  The next getRows call for page 1 will
   *  return this data synchronously instead of hitting the server.
   *  The cache is consumed on first use (single-shot). */
  readonly resetCache: (
    data: readonly Record<string, unknown>[],
    meta?: GridReply['meta']
  ) => void
}

/**
 * Detect pagination state from backend response
 * Backend sends: { data: [...], meta: { next_page, total_count } }
 */
export const detectPaginationState = (reply: GridReply): PaginationState => ({
  hasNextPage: reply.meta.next_page !== null,
  totalCount: reply.meta.total_count
})

/** Always return the actual total count from backend.
 * Previously returned undefined when hasNextPage=true, causing AG Grid to enter
 * infinite scroll mode with stuck "Loading..." skeleton rows that jump and never resolve.
 * Using the actual total_count prevents this SSRM loading state bug. */
const calculateSSRMRowCount = (
  _hasNextPage: boolean,
  totalCount: number
): number => totalCount

/**
 * Check if request has any filters or sorting applied
 * Used to skip initial data cache when user has applied filters/sorting
 */
const hasFiltersOrSorting = (
  request: IServerSideGetRowsParams['request']
): boolean => {
  // Check for filters
  const hasFilters =
    request.filterModel && Object.keys(request.filterModel).length > 0
  // Check for sorting
  const hasSorting = request.sortModel && request.sortModel.length > 0
  return hasFilters || hasSorting
}

/**
 * Create server-side datasource for SSRM with resettable page-1 cache.
 *
 * Returns the datasource and its cache/store lifecycle controls:
 * - `datasource` — the IServerSideDatasource passed to AG Grid
 * - `resetCache(data, meta)` — injects page-1 data so the next
 *    getRows call for page 1 returns it synchronously.
 *    Used by `grid:set-data` just after `refreshServerSide({ purge: true })`,
 *    before its queued load, to avoid a network round-trip for block 0.
 * - `invalidateCache()` — retries pending cells against the current columns.
 * - `retireCache()` — releases pending requests for a replaced store.
 * - `resumeRequests()` — resumes loads after server acceptance or recovery.
 * - `cleanup()` — stops requests and ignores replies after hook retirement.
 */
export const createServerSideDatasource = (
  gridId: string,
  onLoadEvent: string,
  hook: GridTransport,
  target?: string | null,
  paginationSize: number = GRID_DEFAULTS.PAGINATION_SIZE,
  initialData?: InitialDataCache,
  canLoad?: (model: readonly SortModelItem[]) => boolean
): DatasourceWithCache => {
  // Mutable cache state — updated by resetCache, consumed by getRows
  let pageOneCache: InitialDataCache | undefined = initialData
  let cacheConsumed = false
  let active = true
  let generation = 0
  const pending = new Map<GridRowsParams, RowLoad>()

  /** Release cached rows and prevent retired requests from affecting the grid. */
  const cleanup = (): void => {
    active = false
    pageOneCache = undefined
    pending.clear()
  }

  /** Discard cached cells and make pending requests retry against the new view. */
  const invalidateCache = (): void => {
    if (!active) {
      return
    }

    generation += 1
    pageOneCache = undefined
    cacheConsumed = false
  }

  /** Prevent requests for an outgoing store from retrying into its replacement. */
  const retireCache = (): void => {
    if (!active) {
      return
    }

    invalidateCache()
    const requests = [...pending.keys()]
    pending.clear()

    // AG Grid still needs its load slots released after destroying a store.
    // These callbacks belong only to the outgoing cache, never current rows.
    for (const params of requests) {
      params.fail?.()
    }
  }

  /** Complete a native load once and discard any outstanding transport attempt. */
  const complete = (
    params: GridRowsParams,
    data: readonly unknown[],
    rowCount: number
  ): void => {
    pending.delete(params)
    params.success({
      rowData: withRowIndex(data, params.request.startRow ?? 0),
      rowCount
    })
  }

  /** Fetch only accepted sorting, leaving blocked requests in native loading state. */
  const loadRows = (params: GridRowsParams): void => {
    if (
      !(active && pending.has(params)) ||
      (canLoad && !canLoad(params.request.sortModel))
    ) {
      return
    }

    const request = params.request
    const page = calculatePageNumber(request.startRow ?? 0, paginationSize)

    if (
      page === 1 &&
      pageOneCache &&
      !cacheConsumed &&
      (canLoad !== undefined || !hasFiltersOrSorting(request))
    ) {
      cacheConsumed = true
      complete(
        params,
        pageOneCache.data,
        pageOneCache.meta?.total_count ?? pageOneCache.data.length
      )
      return
    }

    const attempt: RowLoad = { status: 'loading', generation }
    pending.set(params, attempt)
    const payload = { id: gridId, page }

    /** Ignore retired attempts and retry changed columns without failing current rows. */
    const callback = (reply: GridReply): void => {
      if (!active || pending.get(params) !== attempt) {
        return
      }

      if (canLoad && !canLoad(request.sortModel)) {
        pending.set(params, { status: 'waiting' })
        return
      }

      if (attempt.generation !== generation) {
        pending.set(params, { status: 'waiting' })
        loadRows(params)
        return
      }

      const { hasNextPage, totalCount } = detectPaginationState(reply)
      complete(
        params,
        reply.data,
        calculateSSRMRowCount(hasNextPage, totalCount)
      )
    }

    if (target) {
      hook.pushEventTo(target, onLoadEvent, payload, callback)
    } else {
      hook.pushEvent(onLoadEvent, payload, callback)
    }
  }

  /** Resume each current load once, without waiting for obsolete transport replies. */
  const resumeRequests = (firstPage?: AcceptedFirstPage): void => {
    for (const [params, state] of [...pending]) {
      if (
        !active ||
        pending.get(params) !== state ||
        (canLoad && !canLoad(params.request.sortModel))
      ) {
        continue
      }

      if (firstPage && (params.request.startRow ?? 0) === 0) {
        complete(params, firstPage.data, firstPage.totalCount)
      } else if (
        state.status === 'waiting' ||
        state.generation !== generation
      ) {
        loadRows(params)
      }
    }
  }

  const datasource = {
    destroy: cleanup,
    getRows: (params: GridRowsParams): void => {
      if (!active || pending.has(params)) {
        return
      }

      pending.set(params, { status: 'waiting' })
      loadRows(params)
    }
  } satisfies IServerSideDatasource<unknown>

  /** Cache accepted page one after the outgoing native store has been replaced. */
  const resetCache = (
    data: readonly Record<string, unknown>[],
    meta?: GridReply['meta']
  ): void => {
    if (!active) {
      return
    }

    retireCache()
    pageOneCache = { data, meta }
  }

  return {
    datasource,
    resetCache,
    invalidateCache,
    retireCache,
    resumeRequests,
    cleanup
  }
}

/**
 * Seed the first SSRM block (root route, startRow 0) with server-inlined page-1
 * data via `applyServerSideRowData`, skipping the block-0 server round-trip.
 *
 * Why this and not the block-0 getRows cache (`createServerSideDatasource`'s
 * `initialData`): intercepting the first getRows hands data back DURING the
 * grid's "loading block 0" sequence, where the rows mount as loading stubs that
 * never transition to data cells until a later refresh. `applyServerSideRowData`
 * pushes the rows into the cache from OUTSIDE that sequence — called on
 * `gridReady`, after columns + cell renderers are live — so they render through
 * the normal "rows arrived" path and paint immediately.
 *
 * `rowCount` MUST be `meta.total_count`: without it SSRM caps the dataset at the
 * inlined page length and never paginates to page 2.
 *
 * Requires the `ServerSideRowModelApiModule` (registered in the grid hook).
 */
export const seedServerSideFirstPage = (
  api: GridApi,
  data: readonly unknown[],
  meta: Pick<GridReply['meta'], 'total_count'>
): void => {
  api.applyServerSideRowData({
    route: [],
    startRow: 0,
    successParams: {
      rowData: withRowIndex(data, 0),
      rowCount: meta.total_count
    }
  })
}

// Export types for external use
export type { DatasourceWithCache, GridReply, PaginationState }
